import { useEffect, useState } from "react";
import { 
  getInventoryPlanningStats, 
  getInventoryPlanningSettings,
  updateInventoryPlanningSettings 
} from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { 
  Package, 
  Store, 
  AlertTriangle, 
  CheckCircle,
  TrendingUp,
  Settings,
  AlertCircle,
  ArrowUp,
  ArrowDown
} from "lucide-react";
import { toast } from "sonner";

export default function InventoryPlanning() {
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Settings form
  const [settingsForm, setSettingsForm] = useState({
    base_qty_per_store: 3,
    safety_buffer_percent: 20,
    low_stock_threshold_multiplier: 1.0,
    reorder_lead_time_days: 30
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsRes, settingsRes] = await Promise.all([
        getInventoryPlanningStats(),
        getInventoryPlanningSettings()
      ]);
      setStats(statsRes.data);
      setSettings(settingsRes.data);
      setSettingsForm(settingsRes.data);
    } catch (error) {
      toast.error("Failed to load inventory planning data");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSettings = async () => {
    try {
      await updateInventoryPlanningSettings(settingsForm);
      toast.success("Settings updated successfully");
      setShowSettings(false);
      loadData();
    } catch (error) {
      toast.error("Failed to update settings");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "critical":
        return <Badge variant="destructive"><AlertTriangle className="w-3 h-3 mr-1" />Critical</Badge>;
      case "low":
        return <Badge className="bg-amber-100 text-amber-800"><AlertCircle className="w-3 h-3 mr-1" />Low</Badge>;
      case "moderate":
        return <Badge className="bg-blue-100 text-blue-800"><ArrowUp className="w-3 h-3 mr-1" />Moderate</Badge>;
      case "healthy":
        return <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle className="w-3 h-3 mr-1" />Healthy</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const filteredRecommendations = stats?.recommendations?.filter(item => {
    const matchesFilter = filter === "all" || item.status === filter;
    const matchesSearch = !searchTerm || 
      item.sku_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.brand?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  }) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="inventory-planning-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 font-heading">Inventory Planning</h2>
          <p className="text-slate-600 mt-1">
            Plan restocks based on {stats?.active_stores || 0} active stores × {settings?.base_qty_per_store || 3} units per SKU
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => setShowSettings(!showSettings)}
          data-testid="planning-settings-btn"
        >
          <Settings className="w-4 h-4 mr-2" />
          Planning Settings
        </Button>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader>
            <CardTitle className="text-lg">Inventory Planning Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label>Base Qty Per Store</Label>
                <Input 
                  type="number" 
                  value={settingsForm.base_qty_per_store}
                  onChange={(e) => setSettingsForm({...settingsForm, base_qty_per_store: parseInt(e.target.value)})}
                  data-testid="base-qty-input"
                />
                <p className="text-xs text-slate-500 mt-1">Units of each SKU per store</p>
              </div>
              <div>
                <Label>Safety Buffer %</Label>
                <Input 
                  type="number" 
                  value={settingsForm.safety_buffer_percent}
                  onChange={(e) => setSettingsForm({...settingsForm, safety_buffer_percent: parseInt(e.target.value)})}
                  data-testid="safety-buffer-input"
                />
                <p className="text-xs text-slate-500 mt-1">Extra stock based on sales</p>
              </div>
              <div>
                <Label>Low Stock Multiplier</Label>
                <Input 
                  type="number" 
                  step="0.1"
                  value={settingsForm.low_stock_threshold_multiplier}
                  onChange={(e) => setSettingsForm({...settingsForm, low_stock_threshold_multiplier: parseFloat(e.target.value)})}
                  data-testid="threshold-multiplier-input"
                />
                <p className="text-xs text-slate-500 mt-1">1.0 = base level threshold</p>
              </div>
              <div>
                <Label>Reorder Lead Time</Label>
                <Input 
                  type="number" 
                  value={settingsForm.reorder_lead_time_days}
                  onChange={(e) => setSettingsForm({...settingsForm, reorder_lead_time_days: parseInt(e.target.value)})}
                  data-testid="lead-time-input"
                />
                <p className="text-xs text-slate-500 mt-1">Days to receive new stock</p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
              <Button onClick={handleUpdateSettings} data-testid="save-planning-settings-btn">Save Settings</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Active Stores</CardTitle>
            <Store className="w-4 h-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900 font-mono" data-testid="planning-active-stores">
              {stats?.active_stores || 0}
            </div>
          </CardContent>
        </Card>

        <Card className={`card-hover ${stats?.summary?.critical > 0 ? 'border-red-300 bg-red-50' : ''}`}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Critical</CardTitle>
            <AlertTriangle className="w-4 h-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold font-mono ${stats?.summary?.critical > 0 ? 'text-red-600' : 'text-slate-900'}`}>
              {stats?.summary?.critical || 0}
            </div>
            <p className="text-xs text-slate-500 mt-1">Out of stock</p>
          </CardContent>
        </Card>

        <Card className={`card-hover ${stats?.summary?.low > 0 ? 'border-amber-300 bg-amber-50' : ''}`}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Low Stock</CardTitle>
            <AlertCircle className="w-4 h-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold font-mono ${stats?.summary?.low > 0 ? 'text-amber-600' : 'text-slate-900'}`}>
              {stats?.summary?.low || 0}
            </div>
            <p className="text-xs text-slate-500 mt-1">Below min level</p>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Moderate</CardTitle>
            <ArrowUp className="w-4 h-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900 font-mono">
              {stats?.summary?.moderate || 0}
            </div>
            <p className="text-xs text-slate-500 mt-1">Consider restocking</p>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Healthy</CardTitle>
            <CheckCircle className="w-4 h-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-emerald-600 font-mono">
              {stats?.summary?.healthy || 0}
            </div>
            <p className="text-xs text-slate-500 mt-1">Adequate stock</p>
          </CardContent>
        </Card>
      </div>

      {/* Top Performers */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-500" />
            Top Performing SKUs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            {stats?.top_performers?.slice(0, 5).map((item, idx) => (
              <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-sm truncate" title={item._id}>{item._id}</p>
                <div className="flex justify-between mt-2">
                  <span className="text-xs text-slate-500">{item.total_qty_sold} sold</span>
                  <span className="text-xs font-mono text-emerald-600">Rs.{item.total_revenue?.toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Filter and Search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex gap-2">
          <Button 
            variant={filter === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter("all")}
          >
            All ({stats?.total_skus || 0})
          </Button>
          <Button 
            variant={filter === "critical" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter("critical")}
            className={filter === "critical" ? "bg-red-600" : ""}
          >
            Critical ({stats?.summary?.critical || 0})
          </Button>
          <Button 
            variant={filter === "low" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter("low")}
            className={filter === "low" ? "bg-amber-600" : ""}
          >
            Low ({stats?.summary?.low || 0})
          </Button>
          <Button 
            variant={filter === "moderate" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter("moderate")}
            className={filter === "moderate" ? "bg-blue-600" : ""}
          >
            Moderate ({stats?.summary?.moderate || 0})
          </Button>
          <Button 
            variant={filter === "healthy" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter("healthy")}
            className={filter === "healthy" ? "bg-emerald-600" : ""}
          >
            Healthy ({stats?.summary?.healthy || 0})
          </Button>
        </div>
        <Input 
          placeholder="Search SKU or brand..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-xs"
          data-testid="planning-search-input"
        />
      </div>

      {/* Recommendations Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading">
            Restock Recommendations ({filteredRecommendations.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-2 font-medium text-slate-600">SKU</th>
                  <th className="text-left py-3 px-2 font-medium text-slate-600">Brand</th>
                  <th className="text-center py-3 px-2 font-medium text-slate-600">Current</th>
                  <th className="text-center py-3 px-2 font-medium text-slate-600">Min Level</th>
                  <th className="text-center py-3 px-2 font-medium text-slate-600">Recommended</th>
                  <th className="text-center py-3 px-2 font-medium text-slate-600">Sold</th>
                  <th className="text-center py-3 px-2 font-medium text-slate-600">Status</th>
                  <th className="text-left py-3 px-2 font-medium text-slate-600">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecommendations.map((item, idx) => (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-2">
                      <span className="font-medium">{item.sku_name}</span>
                    </td>
                    <td className="py-3 px-2 text-slate-600">{item.brand}</td>
                    <td className="py-3 px-2 text-center font-mono">
                      <span className={item.current_stock === 0 ? 'text-red-600 font-bold' : ''}>
                        {item.current_stock}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-center font-mono text-slate-500">{item.min_stock_level}</td>
                    <td className="py-3 px-2 text-center font-mono text-blue-600 font-medium">{item.recommended_stock}</td>
                    <td className="py-3 px-2 text-center font-mono">{item.total_sold}</td>
                    <td className="py-3 px-2 text-center">{getStatusBadge(item.status)}</td>
                    <td className="py-3 px-2 text-slate-600 text-xs">{item.action}</td>
                  </tr>
                ))}
                {filteredRecommendations.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-slate-500">
                      No items match your filter
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
